import {
  type UserConfig,
  type UserConfigColumn,
  getSDK,
  hasSDK,
} from '@littlebird/sdk';

const MIGRATION_MARKER = 'littlebird:user-config-migrated:desktop-local-v1';
const LEGACY_LOCALSTORAGE_KEY = 'littlebird:settings:v1';
const LEGACY_DEVICE_KEY = 'littlebird_settings_v1';
const LEGACY_DEVICE_PREFIX = 'ds:';

type LegacyBlob = Record<string, unknown>;

const FIELD_MAP: Record<UserConfigColumn, Record<string, string>> = {
  feed: {},
  auto_review: {},
  meetings: {
    meetingNotificationFilter: 'notification_filter',
    meetingReminderLeadTime: 'reminder_lead_time',
    meetingDisableReminders: 'disable_reminders',
    excludeSoloEvents: 'exclude_solo_events',
    meetingDisableDetectionNotification: 'disable_detection_notification',
    meetingAutoStartOnDetection: 'auto_start_on_detection',
    meetingAutoStopTimeout: 'auto_stop_timeout',
    meetingStatusWindowOption: 'status_window_option',
  },
  menubar: {
    menubarCalendarEnabled: 'calendar_enabled',
    menubarIncludeEvents: 'include_events',
    menubarPreviewLeadTime: 'preview_lead_time',
  },
  calendar: {
    calendarDaysAhead: 'days_ahead',
    calendarMaxEvents: 'max_events',
    excludedCalendarEventTypes: 'excluded_event_types',
    excludedCalendarStatuses: 'excluded_statuses',
  },
  chat: {
    requireModEnterToSend: 'require_mod_enter_to_send',
    confirmExternalLinks: 'confirm_external_links',
    showThoughts: 'show_thoughts',
    hummingbirdAlwaysNewChat: 'hummingbird_always_new_chat',
  },
  privacy: {
    hideMeetingWidgetFromScreenShare: 'hide_meeting_widget_from_screen_share',
  },
  notifications: {
    notificationSoundEnabled: 'sound_enabled',
  },
  proactive_agents: {},
};

function readBlob(key: string): LegacyBlob | null {
  const raw =
    localStorage.getItem(key) ??
    localStorage.getItem(LEGACY_DEVICE_PREFIX + key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as LegacyBlob;
    }
  } catch {
    // Stale/unreadable blob — skip; defaults will apply.
  }
  return null;
}

function lookup(blobs: LegacyBlob[], legacyKey: string): unknown {
  for (const blob of blobs) {
    const value = blob[legacyKey];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

/**
 * Reads the legacy camelCase blobs (`littlebird:settings:v1` and the
 * `littlebird_settings_v1` device blob in either plain or `ds:`-prefixed form)
 * and maps them into a Partial<UserConfig>. Returns an empty object when no
 * legacy fields are present.
 */
export function collectLegacyUserConfig(): Partial<{
  [C in UserConfigColumn]: Partial<UserConfig[C]>;
}> {
  const blobs = [
    readBlob(LEGACY_LOCALSTORAGE_KEY),
    readBlob(LEGACY_DEVICE_KEY),
  ].filter((blob): blob is LegacyBlob => blob !== null);

  if (blobs.length === 0) return {};

  const partial: Partial<{ [C in UserConfigColumn]: Partial<UserConfig[C]> }> =
    {};

  (Object.keys(FIELD_MAP) as UserConfigColumn[]).forEach((column) => {
    const legacyMap = FIELD_MAP[column];
    const columnPartial: Record<string, unknown> = {};
    for (const [legacyKey, targetKey] of Object.entries(legacyMap)) {
      const value = lookup(blobs, legacyKey);
      if (value !== undefined) {
        columnPartial[targetKey] = value;
      }
    }
    if (Object.keys(columnPartial).length > 0) {
      (partial as Record<string, Record<string, unknown>>)[column] =
        columnPartial;
    }
  });

  return partial;
}

// migrate the settings from local storage to the user config store
export function migrateLegacyDesktopUserConfig(): void {
  if (localStorage.getItem(MIGRATION_MARKER)) return;
  if (!hasSDK()) return;

  const partial = collectLegacyUserConfig();
  try {
    getSDK().userConfig.migrateLegacyConfig(partial);
  } catch {
    // SDK not ready or storage unavailable — leave the marker unset so we
    // retry on the next launch.
    return;
  }

  localStorage.setItem(MIGRATION_MARKER, '1');
}
